<template>
  <div v-tooltip:top :title="helperTitle" class="iblock" data-toggle="tooltip">
    <!--<i v-if="!displayOriginal && !noRatesProvided" class="zmdi zmdi-swap faded" style="vertical-align: text-top; font-size: 0.8em"></i>-->
    <span v-if="showCurrencyCode">
      {{ currencySymbolDisplayText }}
    </span>
    {{ noRatesProvided ? '?' : displayText }}
    <slot name="after-rate"></slot>
  </div>
</template>

<script>
import { UPDATE_USER_STATE } from '@/state/mutation-types'

export default {
  props: {
    forceDisableConversions: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    showConvertedRatesByDefault: { type: Boolean, default: true },
    currencyCode: { type: String, required: true },
    convertedCurrencyCode: { type: String, required: true },
    rate: { type: Number, default: 0 },
    rangeFrom: { type: Number, default: 0 },
    rangeTo: { type: Number, default: 0 },
    exchangeRate: { type: Number, default: 0 },
    showCurrencyCode: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    showHelperTitle: { type: Boolean, default: true },
    ceiling: { type: Boolean, default: false },
    disableConvertCurrencyModal: { type: Boolean, default: false },
  },
  computed: {
    displayText() {
      const rateText = this.displayOriginal ? this.rateText.initial : this.rateText.converted
      if (this.useRange) {
        return rateText
      }
      // format the currency depending on the market settings
      const parts = (rateText.toString() ?? '').split('.')

      const { decimalSeparator } = currentLocale
      return `${this.formatNumber(parts[0])}${decimalSeparator}${
        parts[1]?.replaceAll('.', decimalSeparator)?.replaceAll(',', decimalSeparator) ?? '00'
      }`
    },
    currencySymbolDisplayText() {
      return this.displayOriginal ? this.currencyCode : this.convertedCurrencyCode
    },
    helperTitle() {
      if (this.doNotConvert || !this.showHelperTitle || this.noRatesProvided) {
        return null
      }
      return this.showNonConvertedRatesWithConversionHelper
        ? `* ${$t('Roughly equals')} ${this.convertedCurrencyCode} ${this.rateText.converted}`
        : `* ${$t('Rate in original currency is')} ${this.currencyCode} ${this.rateText.initial}`
    },
    useRange() {
      return !(this.rate > 0)
    },
    ranges() {
      return {
        initial: this.ceiling
          ? `${Math.ceil(this.rangeFrom)} - ${Math.ceil(this.rangeTo)}`
          : `${this.rangeFrom} - ${this.rangeTo}`,
        converted: this.ceiling
          ? `${Math.ceil(this.convertRate(this.rangeFrom))} - ${Math.ceil(
              this.convertRate(this.rangeTo),
            )}`
          : `${this.convertRate(this.rangeFrom).toFixed(
              this.shouldDisplayDecimalPlaces ? 2 : 0,
            )} - ${this.convertRate(this.rangeTo).toFixed(
              this.shouldDisplayDecimalPlaces ? 2 : 0,
            )}`,
      }
    },
    rates() {
      return {
        initial: this.ceiling
          ? Math.ceil(this.rate)
          : this.rate.toFixed(this.shouldDisplayDecimalPlaces ? 2 : 0),
        converted: this.ceiling
          ? Math.ceil(this.convertRate(this.rate))
          : this.convertRate(this.rate).toFixed(this.shouldDisplayDecimalPlaces ? 2 : 0),
      }
    },
    rateText() {
      return {
        initial: this.useRange ? this.ranges.initial : this.rates.initial,
        converted: this.useRange ? this.ranges.converted : this.rates.converted,
      }
    },
    showNonConvertedRatesWithConversionHelper() {
      return !this.showConvertedRatesByDefault
    },
    displayOriginal() {
      return this.doNotConvert || !this.showConvertedRatesByDefault
    },
    doNotConvert() {
      return (
        this.forceDisableConversions ||
        this.currencyCode === this.convertedCurrencyCode ||
        !this.exchangeRatesAvailable
      )
    },
    noRatesProvided() {
      return (
        this.rate + this.rangeFrom + this.rangeTo === 0 ||
        isNaN(this.rate) ||
        isNaN(this.rangeFrom) ||
        isNaN(this.rangeTo)
      )
    },
    exchangeRatesAvailable() {
      return (
        exchangeRates.hasOwnProperty(this.convertedCurrencyCode) &&
        exchangeRates[this.convertedCurrencyCode] > 0 &&
        exchangeRates.hasOwnProperty(this.currencyCode) &&
        exchangeRates[this.currencyCode] > 0
      )
    },
    shouldDisplayDecimalPlaces() {
      // eslint-disable-next-line eqeqeq -- Baseline eslint ignore please fix if working on this
      return this.rate % 1 != 0 || this.rangeFrom % 1 != 0 || this.rangeTo % 1 != 0
    },
  },
  watch: {
    currencySymbolDisplayText(currencyCode) {
      /**
       * @property {string} applied currency code
       */
      this.$emit('applying-currency-code', currencyCode)
    },
  },
  created() {
    if (
      this.currencyCode !== this.convertedCurrencyCode &&
      !this.user.is_impersonating &&
      this.exchangeRatesAvailable &&
      typeof this.user === 'object' &&
      !this.$store.state.user.automatic_currency_conversion &&
      !this.$store.state.user.has_seen_foreign_currency &&
      !this.disableConvertCurrencyModal
    ) {
      this.$store.commit(UPDATE_USER_STATE, {
        fieldName: 'openCurrencyConversionModal',
        fieldValue: true,
      })
    }
    /**
     * @property {string} applied currency code
     */
    this.$emit('applying-currency-code', this.currencySymbolDisplayText)
  },
  methods: {
    convertRate(rate) {
      return (
        Number(rate) *
        (this.exchangeRate ||
          Number(exchangeRates[this.convertedCurrencyCode]) /
            Number(exchangeRates[this.currencyCode]))
      )
    },
    formatNumber(n) {
      // format number 1000000 to 1,234,567
      return n
        .replace(/\D/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, window.currentLocale.thousandsSeparator)
    },
  },
}
</script>

<style scoped lang="scss"></style>
